import { imgSizeList } from '@/common/const';
import { localStorageHelper } from '@/utils/localCache';
import { LocalStorageKey } from '@/types';
import { apiServerLangType, externalLangType, getIntl } from '@/locales';
import { getQueryStringByName } from '@/utils/helper';
import React from 'react';
import { useStore } from '@/store';

/**
 * safeParseJson
 * @param string
 */
export function safeParseJson(string) {
  let result = string;
  try {
    result = JSON.parse(string);
  } catch (error) {
    result = {};
  }
  return result || {};
}

/**
 * 返回一个序列化的字符串，用于计算加密
 * @param api
 * @param params
 */
export function getParamString(api, params) {
  const paramsKeys = Object.keys(params);
  paramsKeys.sort();
  return paramsKeys.reduce((prev, key) => {
    const value = params[key];
    // 如果是 空，或 blob(含 file)，则不加签
    if ([null, undefined].includes(value) || value === '') {
      return prev;
    }
    if (value && typeof value === 'object') {
      return `${prev}${key}${JSON.stringify(value)}`;
    }
    return `${prev}${key}${value}`;
  }, api);
}

/**
 * 返回一个剔除空值成员的对象
 * @param obj
 */
export function removeEmptyValues(obj) {
  return Object.keys(obj).reduce((acc, key) => {
    if (
      obj[key] !== null &&
      obj[key] !== undefined &&
      !(typeof obj[key] === 'object' && Object.keys(obj[key]).length === 0)
    ) {
      acc[key] = obj[key];
    }
    return acc;
  }, {});
}

/**
 * url 参数转对象
 * @param url
 */
export function urlParamsToObject(url) {
  let params = {};
  let parser = document.createElement('a');
  parser.href = url;

  let queryStr = parser.search.substring(1);
  let pairs = queryStr.split('&');

  for (let i = 0; i < pairs.length; i++) {
    let pair = pairs[i].split('=');
    params[pair[0]] = decodeURIComponent(pair[1]);
  }

  return params;
}

export function parseQueryString(queryString) {
  let params = {},
    queries,
    temp,
    i,
    l;

  // Split into key/value pairs
  queries = queryString.split('&');

  // Convert the array of strings into an object
  for (i = 0, l = queries.length; i < l; i++) {
    temp = queries[i].split('=');
    if (params[temp[0]]) {
      if (typeof params[temp[0]] === 'string') {
        params[temp[0]] = [params[temp[0]]];
      }
      params[temp[0]].push(decodeURIComponent(temp[1]));
    } else {
      params[temp[0]] = decodeURIComponent(temp[1]);
    }
  }

  return params;
}

/**
 * 对象转url 参数
 * @param obj
 */
export function objectToQueryString(obj) {
  const keyValuePairs: any = [];
  for (const key in obj) {
    if (Object.hasOwnProperty.call(obj, key)) {
      const value = encodeURIComponent(obj[key] || '');
      keyValuePairs.push(`${key}=${value}`);
    }
  }
  return keyValuePairs.join('&');
}

export const uuid = () => {
  const s: any = [];
  const hexDigits = '0123456789abcdef';
  for (let i = 0; i < 36; i++) {
    s[i] = hexDigits.substr(Math.floor(Math.random() * 0x10), 1);
  }
  s[14] = '4'; // bits 12-15 of the time_hi_and_version field to 0010

  // bits 6-7 of the clock_seq_hi_and_reserved to 01
  s[19] = hexDigits.substr((s[19] && 0x3) || 0x8, 1);

  // s[8] = s[13] = s[18] = s[23] = '-';
  s[8] = '-';
  s[13] = s[8];
  s[18] = s[8];
  s[23] = s[8];

  const uuidString = s.join('').replace(/-/g, '');

  return uuidString;
};

/**
 * @description 获取适配尺寸的图片
 * @param url
 * @param option
 * @returns
 */
export function getFitUrl(
  url,
  option = {
    w: 300,
    q: 75,
  },
) {
  if (!url) return;
  const { w, q } = option;
  if (/(cdn\.icirena\.ai)|(cdn\.yuekeyun\.com)/.test(url)) {
    return `${url}?x-oss-process=image/resize,w_${w}/quality,q_${q ?? 75}`;
  } else if (imgSizeList.includes(w)) {
    return `${url}_${w}x${w}.jpg`;
  } else {
    return url;
  }
}
export function isPromise(obj) {
  return !!obj && (typeof obj === 'object' || typeof obj === 'function') && typeof obj.then === 'function';
}
/**
 * region：区域
  lang：语言
  currency：货币
  tz：时区
  return region=&lang=en&currency=&tz=
 */
export function localeMixin() {
  const urlLang = getQueryStringByName('lang');
  const _currentLang = externalLangType[urlLang] || localStorageHelper.get(LocalStorageKey.lang) || 'zh_TW';
  const region = '';
  const lang = apiServerLangType[_currentLang] || '';
  const currency = '';
  const tz = '';
  const encode = encodeURIComponent(`region=${region}&lang=${lang}&currency=${currency}&tz=${tz}`);
  return encode;
}

export function parseQueryStringLocation(search) {
  const params: any = new URLSearchParams(search);
  const obj = {};

  for (const [key, value] of params.entries()) {
    // 如果参数已经存在（多值情况），则创建一个数组来存储所有值
    if (obj.hasOwnProperty(key)) {
      obj[key] = [].concat(obj[key], value);
    } else {
      obj[key] = value;
    }
  }

  return obj;
}

export function removeQueryParam(search, paramName) {
  // 创建URLSearchParams对象
  const params = new URLSearchParams(search);

  // 删除指定的查询参数
  params.delete(paramName);

  // 返回处理后的查询字符串，带上 '?' 前缀
  return `?${params.toString()}`;
}

export const getSeatNumberRule = (seatSpliceRuleInfo, rowName, columnName, seatInfo?: any, importName?: any) => {
  const intl = getIntl();

  if (seatSpliceRuleInfo) {
    const result = seatSpliceRuleInfo?.value;
    if (!result) return `${rowName}${columnName}`;
    const { rowRule, seatRule } = result;
    return `${rowName}${rowRule}${columnName}${seatRule}`;
  } else {
    if (importName) {
      return importName;
    }
    // {rowId}排{columnId}座
    if (seatInfo) {
      const { column, row, columnId, rowId, name } = seatInfo;
      if (name) {
        return name;
      }
      return intl.formatMessage(
        {
          id: 'iWeb.result.hooks.usePayResultNext.ColumnidRowRowidSeat',
          defaultMessage: '{rowId}排{columnId}座',
        },
        {
          columnId: column || columnId,
          rowId: row || rowId,
        },
      );
    }
    // const seatSpliceRuleInfo = localStorageHelper.get(LocalStorageKey.seatSpliceRuleInfo);
    if (!seatSpliceRuleInfo) return `${rowName}${columnName}`;
    const { rowRule, seatRule } = seatSpliceRuleInfo;
    return `${rowName}${rowRule}${columnName}${seatRule}`;
  }
};

/**
 * 获取座位列表
 */
export const getSeatList = (orderObj) => {
  console.log('log_[orderObj]==>', orderObj);
  const seatNames = orderObj?.order?.ticketInfo?.seatNames || '';
  console.log('log_[seatNames]==>', seatNames);
  const { seatSpliceRuleInfo } = useStore();
  console.log('log_[seatSpliceRuleInfo]==>', seatSpliceRuleInfo);
  if (seatNames) {
    try {
      const { ticketList } = orderObj?.order?.ticketInfo;
      const seatList = ticketList
        ?.map(
          (item) => `${getSeatNumberRule(seatSpliceRuleInfo, item.rowName, item.columnName)} (${item?.ticketTypeName})`,
        )
        ?.join(', ');
      return seatList;
    } catch (error) {
      return '';
    }
  }
  if (!orderObj?.seat) {
    // 后端好像已经处理了-在结果页
    return orderObj
      ?.map((item) => {
        return `${getSeatNumberRule(seatSpliceRuleInfo, item.rowName, item.columnName)} (${item?.ticketTypeName})`;
      })
      ?.join(', ');
  }
  console.log('log_[orderObj.seat?.seatList]==>', orderObj.seat?.seatList);
  // 常规类型
  return orderObj.seat?.seatList
    ?.map((item) => {
      if (item.seatNumber) {
        return item.seatNumber;
      } else {
        return `${getSeatNumberRule(seatSpliceRuleInfo, item.rowName, item.columnName)} (${item?.ticketTypeName})`;
      }
    })
    ?.join(', ');
};

/**
 * 是否是 short 页面
 */
// export const isShortPage = () => {
//   const { search } = window.location;
//   const pattern = /[?&]k=([a-zA-Z0-9]+)/;
//   return pattern.test(search);
// };
export const isShortPage = () => {
  const path = window.location.pathname;
  console.log('log_[path]==>', path);
  return path === '/s';
};
/**
 *
 * @param containerId  容器id
 * @param scrollTo 容器子项id
 * @param docIndex 容器子项索引
 * @returns 容器子项索引
 */
export const scrollToSection = (containerId: string, scrollTo: Set<string>, docIndex: number) => {
  // console.log('scrollToSection-----------', docIndex, scrollTo.size, containerId)

  if (docIndex < 0) {
    return 0;
  }

  if (docIndex + 1 >= scrollTo.size) {
    // console.log('到底了------', docIndex)
    return scrollTo.size - 2;
  }

  // console.log('scrollToSection-----------1', docIndex)
  const contar = document.getElementById(containerId);
  let doc = Array.from(scrollTo)[1];
  let dom = document.getElementById(doc);
  if (!dom) {
    console.log('!dom---------');
    docIndex = 1;
    doc = Array.from(scrollTo)[docIndex];
    dom = document.getElementById(doc);
  }
  if (dom && contar) {
    const x = docIndex * dom.offsetLeft; // 获取元素的左边界位置

    // console.log('dom.offsetLeft--------', x, dom, scrollTo, docIndex)
    contar.scrollTo({
      left: x,
      behavior: 'smooth',
    });

    if (docIndex <= 0) {
      return 0;
    } else if (docIndex >= scrollTo.size - 1) {
      return scrollTo.size - 1;
    }

    // if (parseInt(`${contar.scrollLeft + contar.clientWidth}`) + 10 > parseInt(`${contar.scrollWidth}`)) {
    //   docIndex = 3;
    // } else {
    //   docIndex++;
    // }
  }

  return docIndex;
};

export const lazyImport = (props: any): React.LazyExoticComponent<React.ComponentType<any>> => {
  // const a=props()
  // // console.log(a);
  // debugger
  return React.lazy(props);
};

export const getCurrentDev = () => {
  const { hostname } = window.location;
  let env = '';
  switch (hostname) {
    case 'uat-web.icirena.ai':
      env = 'pre';
      break;
    case 'web.icirena.ai':
    case 'cinematicketing.galaxymacau.com':
      env = 'prod';
      break;
    case 'localhost':
      env = 'daily';
      break;
    default:
      env = 'prod';
      break;
  }
  return env;
};

export const getYoutubeId = (url) => {
  const youtubeUrlRegex = /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/;
  const match = url.match(youtubeUrlRegex);
  if (match && match[1]) {
    return match[1];
  } else {
    return '';
  }
};
